import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { ApiClient } from '../../api/client';
import { Wrench, RefreshCw, Clock, CheckCircle2 } from 'lucide-react';

// ═══════════════════════════════════════════════════════════════
// Karbantartási mód jelzése
//  - superadmin: felső sáv; mindenki más: zároló ablak
//  - azonnal frissül, ha:
//      · a superadmin be-/kikapcsolja (esemény: 'kozad:maintenance-changed'),
//      · bármely mentés karbantartási választ (503, maintenance: true) kap (api/client.ts küldi),
//  - és a superadmin által beállított időközönként (alap: 60 mp) magától is ellenőriz;
//    rejtett böngészőlapon szünetel, visszatéréskor azonnal ellenőriz.
// ═══════════════════════════════════════════════════════════════

export interface MaintenanceState { active: boolean; reason: string; started_at: string | null; check_interval_sec?: number }

export const MAINTENANCE_EVENT = 'kozad:maintenance-changed';

/** Más komponensek (pl. a karbantartás-kapcsoló) ezzel jelzik az új állapotot. */
export function announceMaintenance(state: Partial<MaintenanceState> & { active: boolean }) {
  window.dispatchEvent(new CustomEvent(MAINTENANCE_EVENT, { detail: state }));
}

const DEFAULT_INTERVAL = 60;

export const MaintenanceOverlay: React.FC = () => {
  const { role } = useAuth();
  const [maintenance, setMaintenance] = useState<MaintenanceState>({ active: false, reason: '', started_at: null });
  const [intervalSec, setIntervalSec] = useState(DEFAULT_INTERVAL);
  const [secondsLeft, setSecondsLeft] = useState(DEFAULT_INTERVAL);
  const [checking, setChecking] = useState(false);
  const [lastChecked, setLastChecked] = useState<Date | null>(null);
  const [justEnded, setJustEnded] = useState(false);
  const wasActive = useRef(false);

  const isSuper = role === 'superadmin' || role === 'tenant_admin';

  const apply = useCallback((s: Partial<MaintenanceState> & { active: boolean }) => {
    setMaintenance((prev) => ({
      active: s.active,
      reason: s.reason ?? (s.active ? prev.reason : ''),
      started_at: s.started_at ?? (s.active ? prev.started_at : null),
    }));
    if (s.check_interval_sec) setIntervalSec(Math.max(10, Number(s.check_interval_sec)));
    // a karbantartás vége: rövid visszajelzés a korábban kizárt felhasználónak
    if (wasActive.current && !s.active) {
      setJustEnded(true);
      window.setTimeout(() => setJustEnded(false), 5000);
    }
    wasActive.current = s.active;
  }, []);

  const checkMaintenance = useCallback(async () => {
    setChecking(true);
    try {
      const res: any = await ApiClient.request<MaintenanceState>('/system/maintenance');
      if (res && typeof res.active === 'boolean') {
        apply({
          active: res.active,
          reason: res.reason || '',
          started_at: res.started_at || null,
          check_interval_sec: res.check_interval_sec,
        });
      }
    } catch {
      // hálózati hiba: a következő körben újra próbálja
    } finally {
      setChecking(false);
      setLastChecked(new Date());
    }
  }, [apply]);

  // első ellenőrzés + azonnali jelzések figyelése
  useEffect(() => {
    checkMaintenance();
    const onEvent = (e: Event) => {
      const d = (e as CustomEvent).detail;
      if (d && typeof d.active === 'boolean') apply(d);
    };
    window.addEventListener(MAINTENANCE_EVENT, onEvent);
    return () => window.removeEventListener(MAINTENANCE_EVENT, onEvent);
  }, [checkMaintenance, apply]);

  // időközönkénti ellenőrzés visszaszámlálással; rejtett lapon szünetel
  useEffect(() => {
    setSecondsLeft(intervalSec);
    const tick = window.setInterval(() => {
      if (document.visibilityState !== 'visible') return;
      setSecondsLeft((s) => {
        if (s <= 1) {
          checkMaintenance();
          return intervalSec;
        }
        return s - 1;
      });
    }, 1000);
    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        checkMaintenance();
        setSecondsLeft(intervalSec);
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.clearInterval(tick);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [intervalSec, checkMaintenance]);

  const manualCheck = () => {
    setSecondsLeft(intervalSec);
    checkMaintenance();
  };

  if (!maintenance.active) {
    if (justEnded && !isSuper) {
      return (
        <div role="status" className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[100] px-4 py-2 rounded-xl bg-emerald-600 text-white text-xs font-semibold shadow-lg flex items-center gap-2">
          <CheckCircle2 className="w-4 h-4" /> A karbantartás befejeződött, a rendszer ismét használható.
        </div>
      );
    }
    return null;
  }

  // Szuperadmin: felső sáv (korlátozás nélkül dolgozhat)
  if (isSuper) {
    return (
      <div className="bg-amber-600 text-white px-4 py-1.5 text-xs font-semibold flex items-center justify-between shadow-md z-50 sticky top-0">
        <div className="flex items-center gap-2">
          <Wrench className="w-3.5 h-3.5 animate-spin" />
          <span>FIGYELEM: A Karbantartási Mód jelenleg AKTÍV. (Szuperadminként Ön korlátozás nélkül szerkeszthet.)</span>
          {maintenance.reason && <span className="opacity-90 font-normal">„{maintenance.reason}”</span>}
        </div>
        <span className="text-[10px] bg-black/20 px-2 py-0.5 rounded font-mono">
          Kezdete: {maintenance.started_at ? new Date(maintenance.started_at).toLocaleTimeString() : 'most'}
        </span>
      </div>
    );
  }

  // Mindenki más: zároló ablak, automatikus újraellenőrzéssel
  return (
    <div data-no-esc className="fixed inset-0 z-[100] bg-slate-950/80 backdrop-blur-md flex items-center justify-center p-4 animate-in fade-in duration-200">
      <div role="alertdialog" aria-labelledby="maint-title" className="max-w-md w-full bg-white dark:bg-slate-900 border border-amber-300 dark:border-amber-700/60 rounded-2xl shadow-2xl p-6 text-center space-y-4">
        <div className="w-16 h-16 rounded-2xl bg-amber-500/10 border border-amber-500/30 flex items-center justify-center mx-auto text-amber-600 dark:text-amber-400">
          <Wrench className="w-8 h-8 animate-bounce" />
        </div>

        <div className="space-y-1">
          <h2 id="maint-title" className="text-xl font-bold text-slate-900 dark:text-white">
            Rendszerkarbantartás Folyamatban
          </h2>
          <p className="text-xs text-amber-700 dark:text-amber-400 font-medium">
            Adatbázis-művelet vagy biztonsági visszaállítás zajlik
          </p>
        </div>

        <div className="p-4 rounded-xl bg-slate-50 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700 text-sm text-slate-600 dark:text-slate-300 leading-relaxed">
          {maintenance.reason || 'A rendszer jelenleg zárolva van az adatkonzisztencia és biztonság megőrzése érdekében. Kérjük, várjon néhány percet türelemmel!'}
        </div>

        {maintenance.started_at && (
          <div className="flex items-center justify-center gap-1.5 text-xs text-slate-400">
            <Clock className="w-3.5 h-3.5" />
            <span>Kezdés időpontja: {new Date(maintenance.started_at).toLocaleTimeString()}</span>
          </div>
        )}

        <div className="pt-2 flex flex-col items-center justify-center gap-2">
          <button
            onClick={manualCheck}
            disabled={checking}
            className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-lg bg-indigo-600 hover:bg-indigo-700 disabled:opacity-60 text-white text-xs font-medium transition-colors shadow-sm cursor-pointer"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${checking ? 'animate-spin' : ''}`} />
            <span>{checking ? 'Ellenőrzés…' : 'Újraellenőrzés most'}</span>
          </button>
          <p className="text-[11px] text-slate-400">
            Automatikus ellenőrzés {secondsLeft} mp múlva
            {lastChecked ? ` · utoljára: ${lastChecked.toLocaleTimeString()}` : ''}
          </p>
        </div>
      </div>
    </div>
  );
};
